import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, switchMap } from 'rxjs';
import { FreshdeskHelpModalComponent } from './freshdesk-help-modal/freshdesk-help-modal.component';
import { JobLogService } from '../services/job-log.service';
import { flatten } from 'lodash-es';
import { MatchedArticle } from '../interfaces/matched-article.interface';
import { MissingArticleFeedbackComponent } from './missing-article-feedback/missing-article-feedback.component';
import { getErrorSwal } from '@asamco/utils';
import { LinkDetails } from '../interfaces/link-details.interface';

@Component({
	selector: 'assistance-modal',
	standalone: true,
	imports: [
		CommonModule,
		FreshdeskHelpModalComponent,
		MissingArticleFeedbackComponent
	],
	templateUrl: './assistance-modal.component.html',
	styleUrls: ['./support-styles.scss', './assistance-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistanceModalComponent {
	@ViewChild('helpModal') helpModal?: FreshdeskHelpModalComponent;
	@ViewChild('missingArticleFeedback') missingArticleFeedback?: MissingArticleFeedbackComponent;

	constructor(private joblogService: JobLogService) {

	}

	public title: string;
	public initialErrorText: string;
	public correlationId: string;

	public errorTexts: string[] = [];

	public link$ = new BehaviorSubject<LinkDetails>(null);

	public matches$: BehaviorSubject<MatchedArticle[]> = new BehaviorSubject<MatchedArticle[]>(null);
	public articleIds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(null);
	public showDialog$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	public showError(title: string, errorText: string) {
		const errorSwal = getErrorSwal(errorText);
		this.show(title,  errorSwal.text);
	}

	public show(title: string, errorText: string, correlationId?: string, stackTrace?: string) {
		this.title = (title || '') === '' ? 'An error occurred' : title;
		this.correlationId = correlationId;
		this.initialErrorText = errorText;
		this.errorTexts = [];

		this.matches$.next(null);

		// if correlationId is not already established, we send a request to the backend to log the error, returning the correlationId
		const correlationIdObservable = !!correlationId ? of(correlationId) : this.joblogService.logFrontendException({
			linkId: this.link$.value?.id,
			message: errorText,
			stackTrace: stackTrace
		});

		correlationIdObservable.pipe(
			switchMap(cid => {
				this.correlationId = cid;

				return this.joblogService.getMatchedArticles({
					correlationId: cid,
					matchInput: {
						jobLogType: 4,
						message: errorText,
						fromApplicationId: this.link$.value?.fromApplicationId,
						toApplicationId: this.link$.value?.toApplicationId,
						linkType: this.link$.value?.linkTypeId || 0
					}
				});
			})
		).subscribe({
			next: matches => {
				this.errorTexts = matches.filter(a => (a.displayMessage || '') !== '').map(a => a.displayMessage);
				if(this.errorTexts.length === 0) this.errorTexts = [this.initialErrorText];
				this.matches$.next(matches);
				this.articleIds$.next(flatten(matches.map(m => m.articleIds)));
			},
			error: err => {
				this.errorTexts = [this.initialErrorText];
				this.matches$.next([]);
			}
		});

		this.showDialog$.next(true);
	}

	public close(): void {
		this.showDialog$.next(false);
	}

	public showMoreInformation() {
		const origin = {
			kind: 'link_logs' as const,
			linkId: this.link$.value?.id || 0,
			pattern: {
				id: this.matches$.value[0].patternId ?? null, // there can be multiple pertinent pattern Ids??
			},
			logMessage: this.initialErrorText ?? null,
		};

		this.close();

		this.helpModal?.open('Issue help', flatten(this.matches$.value.map(a => a.articleIds)), origin);
	}

	public requestSupport() {
		const origin = {
			kind: 'link_logs' as const,
			linkId: this.link$.value?.id || 0,
			instanceId: null,
			jobLogType: 4,
			jobLogId: null,
			logMessage: this.initialErrorText,
			pattern: { id: null },
		};

		this.close();

		this.missingArticleFeedback?.open(origin);
	}
}
